import cn from "clsx";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { ReactNode, useId } from "react";
import { EditAttributes } from "design-system/types/types";
import {
  CtaLink,
  CtaLinkProps,
} from "design-system/components/primitives/cta-link/cta-link";
import { Icon } from "design-system/components/icons/icon";

export interface AccordionItemProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  index?: number;
  children?: ReactNode;
  primaryCta?: CtaLinkProps;
  secondaryCta?: CtaLinkProps;
  disabled?: boolean;
  HeadingLevel?: "h2" | "h3" | "h4";
  editAttributes?: {
    title?: EditAttributes;
    subtitle?: EditAttributes;
    children?: EditAttributes;
  };
}

export function AccordionItem({
  children,
  title,
  subtitle,
  index,
  primaryCta,
  secondaryCta,
  disabled = false,
  HeadingLevel = "h3",
  editAttributes,
}: AccordionItemProps) {
  const id = useId();

  const wrapperClass = cn(
    "hbs-accordion-item",
    disabled && "hbs-accordion-item--disabled",
  );
  return (
    <RadixAccordion.Item
      value={id}
      className={wrapperClass}
      data-testid={`item-${index}`}
    >
      <RadixAccordion.Header asChild>
        <RadixAccordion.Trigger
          className="hbs-accordion-item__trigger"
          data-testid={`trigger-${index}`}
          disabled={disabled}
        >
          <div className="hbs-accordion-item__header">
            <HeadingLevel
              className="hbs-accordion-item__title"
              {...editAttributes?.title}
            >
              {title}
            </HeadingLevel>
            {subtitle && (
              <p
                className="hbs-accordion-item__subtitle"
                {...editAttributes?.subtitle}
              >
                {subtitle}
              </p>
            )}
          </div>
          {!disabled && (
            <Icon
              name="plus"
              className="hbs-accordion-item__icon"
              aria-hidden
            />
          )}
        </RadixAccordion.Trigger>
      </RadixAccordion.Header>
      {!disabled && (
        <RadixAccordion.Content
          className="hbs-accordion-item__content"
          {...editAttributes?.children}
        >
          {children}
          {(primaryCta || secondaryCta) && (
            <div className="hbs-accordion-item__ctas">
              {primaryCta && (
                <CtaLink {...primaryCta} type="primary-button" isSmall />
              )}
              {secondaryCta && (
                <CtaLink {...secondaryCta} type="secondary-button" isSmall />
              )}
            </div>
          )}
        </RadixAccordion.Content>
      )}
    </RadixAccordion.Item>
  );
}
